import jsCookie from 'js-cookie'

import { getGlobalConfig } from '@core/globalConfig'
import { isDefinedFalsy } from '@core/helpers/boolean'
import { logInfo } from '@core/logging'
import { isCookiesEphemeral } from '@core/storage/cookiesEphemeral'
import type { cookiesStorages } from '@core/storage/instances/cookiesStorages'
import type { localStorages } from '@core/storage/instances/localStorages'
import type { sessionStorages } from '@core/storage/instances/sessionStorages'
import {
  StorageConsentLevel,
  StorageKeyEssentialEnum,
} from '@core/storage/types'

const CONSENT_COOKIE_EXPIRY_DAYS = 365

type AnyCookieAndStorageInstance = NonNullable<
  | (typeof cookiesStorages)[keyof typeof cookiesStorages]
  | (typeof localStorages)[keyof typeof localStorages]
  | (typeof sessionStorages)[keyof typeof sessionStorages]
>

/**
 * Cookie/LocalStorage/SessionStorage consent handling module class.
 */
export class Cookies {
  #consentCaches: {
    [K in StorageConsentLevel]?: Set<AnyCookieAndStorageInstance>
  } = {}

  // Serves as public docs reference only
  consentLevels: StorageConsentLevel[] = Object.values(StorageConsentLevel)

  /**
   * Consent levels that have been explicitly granted via `setConsent` or
   * `enableCookies`. Exposed on the `window._fwn.cookies` instance for
   * inspection (e.g. diagnostics). Levels not present in either
   * `granted` or `revoked` are considered undecided.
   */
  granted: Set<StorageConsentLevel> = new Set()

  /**
   * Consent levels that have been explicitly revoked via `setConsent` or
   * `disableCookies`. Exposed on the `window._fwn.cookies` instance for
   * inspection (e.g. diagnostics).
   */
  revoked: Set<StorageConsentLevel> = new Set()

  /**
   * Restore the previously granted consent levels (if any) from the
   * `fw_cc` cookie so that user choices survive page
   * reloads. Restoration is opt-out via the `cookies_restore_consent`
   * global config flag — restoration happens by default, set the flag
   * to `false` to disable it.
   */
  constructor() {
    if (!isDefinedFalsy(getGlobalConfig().cookies_restore_consent)) {
      this.#restoreGrantedFromCookie()
    }
  }

  /**
   * Parse comma separated consent levels from a string
   * @param consent
   * @returns
   */
  static parseConsents(consent: string): StorageConsentLevel[] {
    return consent
      .split(',')
      .map((consent) => consent.trim() as StorageConsentLevel)
      .filter((consent) => Object.values(StorageConsentLevel).includes(consent))
  }

  /**
   * Parse comma separated consent string into an array of consents,
   * return all if empty.
   * @param consent
   * @returns
   */
  static parseConsentsOrAll(consent: string): StorageConsentLevel[] {
    return consent && consent.trim().length
      ? Cookies.parseConsents(consent)
      : Object.values(StorageConsentLevel)
  }

  /**
   * Revoke previous consents and set new level of consent.
   * @param consent
   */
  setConsent(consent: string) {
    Object.values(StorageConsentLevel).forEach((level) => {
      this.revoked.add(level)
      this.granted.delete(level)
    })
    Object.values(this.#consentCaches).forEach((caches) =>
      caches?.forEach((cache) => {
        cache.revokeConsent()
        cache.populate({ sweep: true })
      }),
    )

    Cookies.parseConsents(consent).forEach((level) => {
      this.granted.add(level)
      this.revoked.delete(level)
      this.#consentCaches[level]?.forEach((cache) => {
        cache.grantConsent()
        cache.flush()
      })
    })

    this.#persistGrantedToCookie()
  }

  /**
   * Enable cookies storage. Note this is public facing API.
   */
  enableCookies(consent: string) {
    Cookies.parseConsentsOrAll(consent).forEach((level) => {
      this.granted.add(level)
      this.revoked.delete(level)
      this.#consentCaches[level]?.forEach((cache) => {
        cache.grantConsent()
        cache.flush()
      })
    })

    this.#persistGrantedToCookie()
  }

  /**
   * Disable cookies storage. Note this is public facing API.
   */
  disableCookies(consent: string) {
    Cookies.parseConsentsOrAll(consent).forEach((level) => {
      this.revoked.add(level)
      this.granted.delete(level)
      this.#consentCaches[level]?.forEach((cache) => {
        cache.revokeConsent()
        cache.populate({ sweep: true })
      })
    })

    this.#persistGrantedToCookie()
  }

  /**
   * Register consent cache instance for flushing. Newly registered caches
   * inherit the current granted/revoked state so caches that come online
   * after `setConsent`/`enableCookies` (including the restore-from-storage
   * step in the constructor) end up in the correct state.
   * @param instance
   */
  registerConsentCache(
    instance: AnyCookieAndStorageInstance,
    consent: StorageConsentLevel,
  ) {
    if (!this.#consentCaches[consent]) {
      this.#consentCaches[consent] = new Set()
    }
    this.#consentCaches[consent]?.add(instance)

    if (this.granted.has(consent)) {
      instance.grantConsent()
      instance.flush()
    } else if (this.revoked.has(consent)) {
      instance.revokeConsent()
      instance.populate({ sweep: true })
    }
  }

  /**
   * Persist the currently granted consent levels to the `fw_cc` cookie so
   * they can be restored on the next page load / tab session.
   *
   * When `cookies_ephemeral` is enabled the cookie is written without an
   * `expires` attribute, making it a session cookie that the browser
   * discards when the session ends.
   *
   * Bypasses the consent-gated `CookiesCache` on purpose: this snapshot
   * IS the consent and so is strictly necessary by definition. Going
   * through the cache layer would only buffer the value in memory
   * whenever the essential cache is not (yet) consented — opt-in mode,
   * right after `setConsent`/`disableCookies` revokes it, or when a
   * caller only grants a non-essential level like
   * `enableCookies('functional')`. The snapshot would then be lost on
   * the next reload, defeating the entire restore mechanism.
   */
  #persistGrantedToCookie() {
    if (typeof document === 'undefined') {
      return
    }
    const key = StorageKeyEssentialEnum.fw_cc
    const value = [...this.granted].join(',')
    try {
      if (value) {
        const opts = isCookiesEphemeral()
          ? undefined
          : { expires: CONSENT_COOKIE_EXPIRY_DAYS }
        jsCookie.set(key, value, opts)
      } else {
        jsCookie.remove(key)
      }
    } catch (_) {
      // best effort — cookie write may throw (e.g. unsupported environment)
    }
  }

  /**
   * Restore previously granted consent levels from the `fw_cc` cookie.
   * Symmetric with `#persistGrantedToCookie` — reads directly so the
   * snapshot is available before any consent decision has been made.
   */
  #restoreGrantedFromCookie() {
    if (typeof document === 'undefined') {
      return
    }
    try {
      const value = jsCookie.get(StorageKeyEssentialEnum.fw_cc)
      if (value) {
        this.setConsent(value)
        logInfo('Restored granted consent levels from cookie', value)
      }
    } catch (_) {
      // best effort — cookie read may throw (e.g. unsupported environment)
    }
  }

  /**
   * Testing only. List all registered consent cache instances.
   * @returns
   */
  listCacheInstances() {
    return this.#consentCaches
  }
}
